import React from 'react';
import { StyleSheet, View } from 'react-native';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import ExpressiveXSText from 'shared/components/vxl/typography/expressive/ExpressiveXSText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

export interface StackedTextAccessoryProps {
  /**
   * appears above the title
   */
  labelText: string;
  titleText?: Maybe<string>;
  textStyle?: 'primary' | 'secondary';
  alignment?: 'leading' | 'trailing';
  labelIconKey?: Maybe<IconKeyType>;
  iconOnPress?: Maybe<() => void>;
}

const containerAlignmentStyle = (
  alignment: StackedTextAccessoryProps['alignment'],
) => {
  if (alignment === 'leading') {
    return styles.containerAlignLeading;
  }
  return styles.containerAlignTrailing;
};

const labelContainerAlignmentStyle = (
  alignment: StackedTextAccessoryProps['alignment'],
) => {
  if (alignment === 'leading') {
    return styles.labelContainerAlignLeading;
  }
  return styles.labelContainerAlignTrailing;
};

const textAlignmentStyle = (
  alignment: StackedTextAccessoryProps['alignment'],
) => {
  if (alignment === 'leading') {
    return styles.textAlignLeading;
  }
  return styles.textAlignTrailing;
};

const textColorStyle = (textStyle: StackedTextAccessoryProps['textStyle']) => {
  if (textStyle === 'primary') {
    return styles.textStylePrimary;
  }
  return styles.textStyleSecondary;
};

const IconAccessory = (
  props: Pick<StackedTextAccessoryProps, 'labelIconKey' | 'iconOnPress'>,
) => {
  const { labelIconKey, iconOnPress } = props;

  if (!labelIconKey) {
    return null;
  }

  if (iconOnPress) {
    return (
      <IconButton
        iconKey={labelIconKey}
        iconSize="MD"
        iconOnPress={iconOnPress}
        iconColor={theme.color.text.secondary}
        // TODO: add accessibility label for icon types
        // https://varomoney.atlassian.net/browse/JT-122746
        iconButtonAccessibilityLabel=""
      />
    );
  }

  return (
    <Icon
      iconKey={labelIconKey}
      iconSize="MD"
      iconColor={theme.color.text.secondary}
    />
  );
};

const StackedTextAccessory = ({
  labelText,
  titleText,
  alignment = 'leading',
  textStyle = 'primary',
  labelIconKey,
  iconOnPress,
}: StackedTextAccessoryProps) => {
  return (
    <View style={[styles.container, containerAlignmentStyle(alignment)]}>
      <View
        style={[styles.labelContainer, labelContainerAlignmentStyle(alignment)]}
      >
        {/* switch the placement of the icon before or after based on alignment */}
        {alignment === 'trailing' ? (
          <IconAccessory
            labelIconKey={labelIconKey}
            iconOnPress={iconOnPress}
          />
        ) : null}
        {labelText ? (
          <BodySmText
            style={[textColorStyle(textStyle), textAlignmentStyle(alignment)]}
            numberOfLines={1}
          >
            {labelText}
          </BodySmText>
        ) : null}
        {alignment === 'leading' ? (
          <IconAccessory
            labelIconKey={labelIconKey}
            iconOnPress={iconOnPress}
          />
        ) : null}
      </View>
      {titleText ? (
        <ExpressiveXSText
          style={[textColorStyle(textStyle), textAlignmentStyle(alignment)]}
          numberOfLines={1}
        >
          {titleText}
        </ExpressiveXSText>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: theme.spacing.xs,
  },
  containerAlignLeading: {
    alignItems: 'flex-start',
  },
  containerAlignTrailing: {
    alignItems: 'flex-end',
  },
  labelContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    // this overflows on web if not set to 100%
    width: '100%',
  },
  labelContainerAlignLeading: {
    justifyContent: 'flex-start',
  },
  labelContainerAlignTrailing: {
    justifyContent: 'flex-end',
  },
  textAlignLeading: {
    flexWrap: 'wrap',
    textAlign: 'left',
  },
  textAlignTrailing: {
    flexWrap: 'wrap',
    textAlign: 'right',
  },
  textStylePrimary: {
    color: theme.color.text.primary,
  },
  textStyleSecondary: {
    color: theme.color.text.secondary,
  },
});

export default StackedTextAccessory;
